import * as _ from 'lodash';
import * as AsyncLock from 'async-lock';
import {ILease} from "./PA.UtilsIF";
import {GenericLease} from "./GenericLease";
import {LeaseRegistry} from "./LeaseRegistry";
import {PAdbContext} from "../PA.Core/PAdbContext";
import {PASearch} from "../PA.Core/PASearch";
import {WB_PeriodRow} from "../PA.Core/PAdbSchemaRows";
import {ProductHierarchyLink} from "./ProductHierarchyContext";
import {Period_Hierarchy_Max_Depth} from "../PA.MD.Core/MD.Constants";
import {PeriodService} from "../PA.MD.Services/PeriodService";
import {DateUtils} from "../PA.Core/Basic/Dates";

export class PeriodHierarchyLink {
    relWbID: number;
    childWbID: number;
    parentWbID: number;
    childPeriodMWbID: number;
    parentPeriodMWbID: number;

    static copyLink(anOrigLink: PeriodHierarchyLink, aNewLink: PeriodHierarchyLink) {
        aNewLink.relWbID = anOrigLink.relWbID;
        aNewLink.parentWbID = anOrigLink.parentWbID;
        aNewLink.parentPeriodMWbID = anOrigLink.parentPeriodMWbID;
        aNewLink.childWbID = anOrigLink.childWbID;
        aNewLink.childPeriodMWbID = anOrigLink.childPeriodMWbID;
    }
}

export class PeriodHierarchyContext {

    private readonly periodHierarchyWbID: number;
    private _bIsLoaded: boolean = false;
    private dctParentLinkByChildWbID: {[childWbid: number]: PeriodHierarchyLink} = {};
    private dctChildLinksByParentWbID: {[parentWbid: number] : PeriodHierarchyLink[]} = {};
    private dctChildLinksByParentWbIDLevelID: {[parentWBid: number]: {[periodMWbID: number]: PeriodHierarchyLink[]}} = {};
    private dctParentLinksByChildWbIDLevelID: {[childWBid: number]: {[periodMWbID: number]: PeriodHierarchyLink}} = {};
    private dctWbPeriodMIdToDateRangeToPeriods: {[wbPeriodMId: number]: {[dateKey: string]: WB_PeriodRow[]}} = {}

    private _lease: ILease;
    private dctLock: any = new AsyncLock();
    private _dbContext: PAdbContext;

    constructor(periodHierarchyWbID: number) {
        this.periodHierarchyWbID = periodHierarchyWbID;
        this._dbContext = new PAdbContext({bDisableTracking: true});
        this.createLease();
    }

    async getPeriodsOfType(wbPeriodMID: number) : Promise<WB_PeriodRow[]> {
        const self = this;
        let retVal: WB_PeriodRow[];
        await self.dctLock.acquire('global',
        (done: any) => {
            self.checkLease()
                .then((ignore) => {
                    if (!self._bIsLoaded) {
                        return self.load();
                    }
                })
                .then((ignore) => {
                    done(null, null);
                    return;
                })
        });
        let nodeIds: number[] = this.getNodes(wbPeriodMID);
        retVal = await PeriodService.getPeriodsByWBID(nodeIds);

        return retVal;
    }

    /**
     * Returns all periods in this hierarchy that are at the given level and overlap the given date range
     */
    async getPeriodsOfTypeByDateRange(wbPeriodMID: number, startDt: Date, endDt: Date) : Promise<WB_PeriodRow[]> {
        const self = this;
        await self.dctLock.acquire('global',
            (done: any) => {
                self.checkLease()
                    .then((ignore) => {
                        if (!self._bIsLoaded) {
                            return self.load();
                        }
                    })
                    .then((ignore) => {
                        done(null, null);
                        return;
                    })
            });
        let key: string = `${DateUtils.convertToSAPBW(startDt)}-${DateUtils.convertToSAPBW(endDt)}`;
        if (!(wbPeriodMID in self.dctWbPeriodMIdToDateRangeToPeriods)) {
            self.dctWbPeriodMIdToDateRangeToPeriods[wbPeriodMID] = {};
        }

        if (key in self.dctWbPeriodMIdToDateRangeToPeriods[wbPeriodMID]) {
            return self.dctWbPeriodMIdToDateRangeToPeriods[wbPeriodMID][key];
        } else {
            //if not cached, find correct period types and filter for the desired ones
            let periodsAtLevel: WB_PeriodRow[] = await PeriodService.getPeriodsByWBID(this.getNodes(wbPeriodMID));
            let retVal: WB_PeriodRow[] = _.filter(periodsAtLevel, (period) => {return DateUtils.RangeDatesOverlap(startDt, endDt, period.PeriodStartDt, period.PeriodEndDt)});
            self.dctWbPeriodMIdToDateRangeToPeriods[wbPeriodMID][key] = retVal;
            return retVal;
        }
    }

    getPeriodParent(wbPeriodID: number, parentwbPeriodMId?: number) : Promise<number> {
        const self = this;
        return new Promise<number>((resolve, error) => {

            self.dctLock.acquire('global',
                (done: any) => {
                    self.checkLease()
                        .then((ignore) => {
                            if (_.isUndefined(parentwbPeriodMId)) {
                                return self.getImmediatePeriodParent(wbPeriodID)
                            } else {
                                return self.getPeriodParentAtLevel(wbPeriodID, parentwbPeriodMId);
                            }
                        })
                        .then((result) => {
                            if (!_.isUndefined(result)) {
                                done(null, wbPeriodID);
                                resolve(result.parentWbID);
                                return;
                            } else {
                                done(null, wbPeriodID);
                                resolve(undefined);
                                return;
                            }
                        })
                        .catch((err) => {
                            error(err);
                        })
                })

        })
    }

    getChildren(periodWbID: number, childPeriodMWbID?: number, depthLimit?: number) : Promise<number[]> {
        const self = this;
        return new Promise<number[]>((resolve, error) => {

            self.dctLock.acquire('global',
                (done: any) => {
                    self.checkLease()
                        .then((ignore) => {
                            if (_.isUndefined(childPeriodMWbID)) {
                                return self.getImmediatePeriodChildren(periodWbID);
                            } else {
                                return self.getChildrenAtLevel(periodWbID, childPeriodMWbID, depthLimit)
                            }
                        })
                        .then((results) => {
                            done(null, null);
                            let retVal: number[] = _.map(results, (aVal) => {return aVal.childWbID});
                            resolve(retVal);
                        })
                        .catch((err) => {
                            error(err);
                        })
                })

        })
    }


    //<editor-fold desc="Private methods">
    private async getImmediatePeriodParent(periodWbID: number) : Promise<PeriodHierarchyLink> {
        const self = this;
        return new Promise<PeriodHierarchyLink>((resolve, error) => {

            if (periodWbID in self.dctParentLinkByChildWbID) {
                resolve(self.dctParentLinkByChildWbID[periodWbID]);
                return;
            } else if (!(periodWbID in self.dctParentLinkByChildWbID) && self._bIsLoaded) {
                resolve(undefined);
                return;
            }

            self.load()
                .then((ignore) => {
                    resolve(self.dctParentLinkByChildWbID[periodWbID]);
                    return;
                })
                .catch((err) => {
                    error(err);
                })
        })
    }

    private async getPeriodParentAtLevel(periodWbID: number, parentPeriodMWbID: number) : Promise<PeriodHierarchyLink> {

        if (periodWbID in this.dctParentLinksByChildWbIDLevelID) {
            if (parentPeriodMWbID in this.dctParentLinksByChildWbIDLevelID[periodWbID]) {
                return this.dctParentLinksByChildWbIDLevelID[periodWbID][parentPeriodMWbID];
            }
        }

        let retVal: PeriodHierarchyLink;
        await this.climbToPeriodParent(periodWbID, parentPeriodMWbID, retVal);

        if (!_.isUndefined(retVal)) {
            if (!(periodWbID in this.dctParentLinksByChildWbIDLevelID)) {
                this.dctParentLinksByChildWbIDLevelID[periodWbID] = {};
            }
            if (!(parentPeriodMWbID in this.dctParentLinksByChildWbIDLevelID[periodWbID])) {
                this.dctParentLinksByChildWbIDLevelID[periodWbID][parentPeriodMWbID] = retVal;
            }
        }

        return retVal;
    }

    private async climbToPeriodParent(wbPeriodID: number, wbParentPeriodMID: number, retVal: PeriodHierarchyLink){

        let currentPrntWbID: number = wbPeriodID;
        let currentPrntPeriodMWbID: number = -1;
        let currentLink: PeriodHierarchyLink;
        let remainingDepth = Period_Hierarchy_Max_Depth;

        while (wbParentPeriodMID != currentPrntPeriodMWbID) {
            currentLink = await this.getImmediatePeriodParent(wbPeriodID);

            if (!_.isUndefined(currentLink)) {
                if (currentLink.parentPeriodMWbID == wbParentPeriodMID) {
                    PeriodHierarchyLink.copyLink(currentLink, retVal);
                    break;
                }

                currentPrntWbID = currentLink.parentWbID;
                currentPrntPeriodMWbID = currentLink.parentPeriodMWbID;

                remainingDepth -= 1;
                if (remainingDepth < 0) {
                    break;
                }
            }
        }
    }

    private getImmediatePeriodChildren(periodWbID: number) : Promise<PeriodHierarchyLink[]> {
        const self = this;
        return new Promise<PeriodHierarchyLink[]>((resolve, error) => {
            if (periodWbID in self.dctChildLinksByParentWbID) {
                resolve(self.dctChildLinksByParentWbID[periodWbID]);
                return;
            } else if (self._bIsLoaded && !(periodWbID in self.dctChildLinksByParentWbID)) {
                resolve([]);
                return;
            }

            self.load()
                .then((ignore) => {
                    resolve(self.dctChildLinksByParentWbID[periodWbID]);
                    return;
                })
                .catch((err) => {
                    error(err);
                })
        })
    }

    private async getChildrenAtLevel(wbPeriodID: number, wbChildPeriodMID: number, depthlimit?: number) : Promise<PeriodHierarchyLink[]> {

        if (wbPeriodID in this.dctChildLinksByParentWbIDLevelID) {
            if (wbChildPeriodMID in this.dctChildLinksByParentWbIDLevelID[wbPeriodID]) {
                return this.dctChildLinksByParentWbIDLevelID[wbPeriodID][wbChildPeriodMID];
            }
        }

        let retVal: PeriodHierarchyLink[] = [];
        await this.recurseToChildrenAtLevel(wbPeriodID, wbChildPeriodMID, depthlimit || Period_Hierarchy_Max_Depth, retVal);

        if (!(wbPeriodID in this.dctChildLinksByParentWbIDLevelID)) {
            this.dctChildLinksByParentWbIDLevelID[wbPeriodID] = {};
        }
        this.dctChildLinksByParentWbIDLevelID[wbPeriodID][wbChildPeriodMID] = retVal;

        return retVal;
    }

    private async recurseToChildrenAtLevel(wbPeriodID: number, wbChildPeriodMID: number, depthLimit: number, retVal: PeriodHierarchyLink[]) {
        if (depthLimit < 0) {
            return;
        }
        let newDepth: number = depthLimit - 1;
        let currentLinkList: PeriodHierarchyLink[] = await this.getImmediatePeriodChildren(wbPeriodID);

        if (_.isUndefined(currentLinkList)) {
            return;
        }

        for (let aLink of currentLinkList) {
            if (aLink.childPeriodMWbID == wbChildPeriodMID) {
                retVal.push(aLink);
            } else {
                await this.recurseToChildrenAtLevel(aLink.childWbID, wbChildPeriodMID, newDepth, retVal);
            }
        }
    }

    private getNodes(periodMWbID: number) : number[] {

        let dctResults: {[id: number]: number} = {};
        _.forEach(this.dctParentLinkByChildWbID, (anEntry, aKey) => {
            if (anEntry.childPeriodMWbID == periodMWbID) {
                dctResults[anEntry.childWbID] = anEntry.childWbID;
            } else if (anEntry.parentPeriodMWbID == periodMWbID) {
                dctResults[anEntry.parentWbID] = anEntry.parentWbID;
            }
        });
        return _.values(dctResults);
    }
    //</editor-fold>

    //<editor-fold desc="Lease management">

    private async load() : Promise<void> {
        let results: any[] = await PASearch.find("vWB_PeriodR_Props",
            {WB_PERIODH_ID: this.periodHierarchyWbID},
            {WB_PERIODH_ID: {type: "number"}},
            [], 0,
            []);

        const self = this;

        _.forEach(results, (aResult) => {
            let linkObject: PeriodHierarchyLink = new PeriodHierarchyLink();
            linkObject.childWbID = aResult.WB_PERIOD_ID;
            linkObject.childPeriodMWbID = aResult.WB_PERIODM_ID_CHILD;
            linkObject.relWbID = aResult.WB_PERIODR_ID;
            linkObject.parentWbID = aResult.WB_PERIOD_ID_PRNT;
            linkObject.parentPeriodMWbID = aResult.WB_PERIODM_ID_PRNT;

            if (!(linkObject.childWbID in self.dctParentLinkByChildWbID)) {
                self.dctParentLinkByChildWbID[linkObject.childWbID] = linkObject;
            }

            if (!(linkObject.parentWbID in self.dctChildLinksByParentWbID)) {
                self.dctChildLinksByParentWbID[linkObject.parentWbID] = [];
            }
            self.dctChildLinksByParentWbID[linkObject.parentWbID].push(linkObject);
        });

        self._bIsLoaded = true;
        self._lease.resetLease();
    }

    private reset() {
        this._bIsLoaded = false;
        this.dctChildLinksByParentWbID = {};
        this.dctChildLinksByParentWbIDLevelID = {};
        this.dctParentLinkByChildWbID = {};
        this.dctParentLinksByChildWbIDLevelID = {};
    }

    private createLease() {
        this._lease = new GenericLease(LeaseRegistry.Lease_Type_Period_Hierarchy);
        this._lease.addClasses(PeriodHierarchyContext.getLeaseClasses(this.periodHierarchyWbID));
    }

    private async checkLease() : Promise<void> {
        if (await this._lease.hasDataChanged()){
            this.reset();
        }
    }

    static getLeaseClasses(periodHWbID: number) : string[] {
        let retVal: string[] = [];
        retVal.push(LeaseRegistry.getLeaseClass(LeaseRegistry.Lease_Subject_Period_Hierarchy, periodHWbID));
        return retVal;
    }
    //</editor-fold>

}